import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@devhop/ui";
import { useEffect, useState } from "react";
import { useBulkVoidCreditInvoicesMutation } from "../api/queries";

export type BulkVoidCreditInvoicesDialogProps = {
  invoiceIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (result: {
    succeededCount: number;
    failedCount: number;
  }) => void;
};

export function BulkVoidCreditInvoicesDialog({
  invoiceIds,
  open,
  onOpenChange,
  onSuccess,
}: BulkVoidCreditInvoicesDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mutation = useBulkVoidCreditInvoicesMutation();

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("ກະລຸນາປ້ອນເຫດຜົນ");
      return;
    }
    if (invoiceIds.length === 0) return;

    setError(null);
    try {
      const result = await mutation.mutateAsync({
        invoiceIds,
        reason: trimmed,
      });
      onOpenChange(false);
      onSuccess?.({
        succeededCount: result.succeeded.length,
        failedCount: result.failed.length,
      });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "ຍົກເລີກຫຼາຍໃບລົ້ມເຫຼວ",
      );
    }
  };

  const isSubmitting = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>ຍົກເລີກຫຼາຍໃບ</DialogTitle>
          <p className="text-muted-foreground text-sm">
            ຈະຍົກເລີກ {invoiceIds.length} ໃບ — ຍ້ອນກັບແພັກ/ຈອງຕາມປະເພດອ້າງອີງ (ບໍ່
            ຮອງຮັບ partner)
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/30 p-4 text-muted-foreground text-sm">
            ແຕ່ລະໃບຈະ void ການຮັບເງິນ, ຍົກເລີກແຜນງວດ, ແລະ ລຶບ/ຍົກເລີກຂໍ້ມູນທີ່ຜູກກັນ
            ເຫມືອນຍົກເລີກໃບເຕັມ
          </div>

          <div className="space-y-2">
            <Label htmlFor="bulk-void-credit-reason">
              ເຫດຜົນ <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="bulk-void-credit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ປ້ອນເຫດຜົນການຍົກເລີກ..."
              rows={4}
              className="resize-none"
              disabled={isSubmitting}
            />
          </div>

          {error ? <p className="text-destructive text-sm">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            ປິດ
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isSubmitting || invoiceIds.length === 0}
          >
            {isSubmitting ? "ກຳລັງຍົກເລີກ..." : "ຢືນຢັນຍົກເລີກ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
